declare global {
  interface Window {
    __lastOrderPayload?: any;
  }
}
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { useOrderActions } from '../../../../contexts/OrderActionsContext';
import { OrderSchemaType, orderSchema } from './order.schema';
import { ItemOrderSchemaType } from './order.schema';
import { createSteelOrder, fetchServices, fetchScripts, fetchOrderById, updateSteelOrder } from './api';
import { itemInitialState } from './constants';

import { Card, Separator, Button, Container, AddNewItemButton } from './style';
import MainInfosSection from './Sections/MainInfosSection';
import DetailsSection from './Sections/DetailsSection';
import FreteSection from './Sections/FreteSection';
import PaymentSection from './Sections/PaymentSection';
import { ItemsSection } from './Sections/Items/index';
import { ApiDataProvider } from './contexts/ApiDataContext';
import ConfirmationModal from '../../../../components/ModalConfirmation';



const AddOrder: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { orderId } = useParams<{ orderId: string }>();
  const { setFeedback } = useContext(FeedbackContext);
  const { setSaveOrderHandler } = useOrderActions();

  const isEditMode = Boolean(orderId);

  const { data: existingOrder, isLoading: isLoadingOrder } = useQuery({
    queryKey: ['steelOrder', orderId],
    queryFn: () => fetchOrderById(orderId!),
    enabled: isEditMode,
    staleTime: 0,
  });

  const { mutate: submitOrder, isPending: isSubmitting } = useMutation({
    mutationFn: isEditMode
      ? (data: OrderSchemaType) => updateSteelOrder(orderId!, data)
      : createSteelOrder,
    onSuccess: (data) => {
      const action = isEditMode ? 'atualizado' : 'criado';
      setFeedback({ message: `Pedido ${data.id || ''} ${action} com sucesso!`, isOpen: true, statusOk: true });
      if (returnTo) {
        navigate(returnTo, { replace: true });
      } else {
        navigate('/panel/steel');
      }
    },
    onError: (error) => {
      const action = isEditMode ? 'atualizar' : 'criar';
      setFeedback({ message: `Erro ao ${action} pedido: ${error.message}`, isOpen: true, statusOk: false });
    },
  });

  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [isNoItemsModalOpen, setIsNoItemsModalOpen] = useState(false);
  const [noSavedItemsType, setNoSavedItemsType] = useState<'no-items' | 'not-saved'>('no-items');
  const [collapsedItems, setCollapsedItems] = useState<Set<number>>(new Set([0]));
  const [hasValidationError, setHasValidationError] = useState(false);

  useEffect(() => {
    if (existingOrder?.items && isEditMode) {
      const initialCollapsedState = new Set<number>();
      existingOrder.items.forEach((_, index) => {
        initialCollapsedState.add(index);
      });
      setCollapsedItems(initialCollapsedState);
    }
  }, [existingOrder?.items?.length, isEditMode]);



  const methods = useForm<OrderSchemaType>({
    resolver: zodResolver(orderSchema),
    defaultValues: {
      items: [itemInitialState],
      attachments: [],
      shippingValue: 0,
    },
    mode: 'onBlur',
  });

  const { control, handleSubmit, formState: { errors } } = methods;

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'items',
  });

  useEffect(() => {
    if (fields.length > 0) {
      const currentCollapsed = new Set(collapsedItems);
      let hasChanges = false;

      fields.forEach((_, index) => {
        if (!currentCollapsed.has(index)) {
          currentCollapsed.delete(index);
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setCollapsedItems(currentCollapsed);
      }
    }
  }, [fields]);

  const servicesQuery = useQuery({
    queryKey: ['services'],
    queryFn: fetchServices,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const scriptsQuery = useQuery({
    queryKey: ['scripts'],
    queryFn: fetchScripts,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const onValidSubmit = useCallback((data: OrderSchemaType) => {
    let itemsToSend = [];
    if (data.items && Array.isArray(data.items)) {
      itemsToSend = data.items.map((item, idx) => {
        const savedSubItems = Array.isArray(item.subItems)
          ? item.subItems.filter((sub: ItemOrderSchemaType) => sub.isSaved)
          : [];

        let quantity = 0;
        if (item.itemType === 'MATERIAL') {
          quantity = (typeof item.feedstock?.quantidade === 'number') ? item.feedstock.quantidade : 0;
        } else if (item.itemType === 'PRODUCT') {
          quantity = (typeof item.finalProduct?.quantidade === 'number') ? item.finalProduct.quantidade : 0;
        } else if (item.itemType === 'RESOURCE') {
          quantity = (typeof item.resource?.quantidade === 'number') ? item.resource.quantidade : 0;
        } else if (item.itemType === 'SERVICE') {
          quantity = (typeof item.stage?.quantidade === 'number') ? item.stage.quantidade : 0;
        }

        let filteredRequestedProduct: any = undefined;
        if (item.requestedProduct && typeof item.requestedProduct === 'object') {
          const filtered: any = {};
          if ('id' in item.requestedProduct) filtered.id = item.requestedProduct.id;
          if ('name' in item.requestedProduct) filtered.name = item.requestedProduct.name;
          filteredRequestedProduct = Object.keys(filtered).length > 0 ? filtered : undefined;
        }

        let itemWithoutRequestedOrOffered = item;
        if (item.itemType === 'MATERIAL') {
          const { requestedProductMaterial, offeredProductMaterial, ...rest } = item;
          itemWithoutRequestedOrOffered = rest;
        } else {
          const { requestedProduct: _omitRequestedProduct, requestedProductMaterial: _omitRequestedProductMaterial, ...rest } = item;
          itemWithoutRequestedOrOffered = rest;
        }

        const shouldSendRequestedProductMaterial = item.itemType !== 'MATERIAL' || savedSubItems.length > 0;
        const shouldSendRequestedProduct = item.itemType !== 'MATERIAL' || (savedSubItems.length > 0 && filteredRequestedProduct);

        const itemToSend = {
          ...itemWithoutRequestedOrOffered,
          ...(item.feedstock ? { feedstock: item.feedstock } : {}),
          ...(shouldSendRequestedProductMaterial && item.requestedProductMaterial ? { requestedProductMaterial: item.requestedProductMaterial } : {}),
          ...(shouldSendRequestedProduct && filteredRequestedProduct ? { requestedProduct: filteredRequestedProduct } : {}),
          quantity,
          subItems: savedSubItems
        };
        return itemToSend;
      });
    }

    const savedItems = itemsToSend.filter(item => item.isSaved === true);
    if (savedItems.length === 0) {
      setHasValidationError(true);

      if (itemsToSend.length === 0) {
        setNoSavedItemsType('no-items');
      } else {
        setNoSavedItemsType('not-saved');
      }
      setIsNoItemsModalOpen(true);
      return;
    }

    // Bloqueia salvar se a soma das parcelas != total ou houver parcela zerada.
    const parcelas = Array.isArray(data.installments) ? data.installments : [];
    if (parcelas.length > 0) {
      const orderTotal = Number(data.total) || 0;
      const somaParcelas = parcelas.reduce((acc: number, p: any) => acc + (Number(p?.valor) || 0), 0);
      const hasParcelaZerada = parcelas.some((p: any) => !(Number(p?.valor) > 0));
      const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

      if (hasParcelaZerada || Math.abs(somaParcelas - orderTotal) > 0.01) {
        setHasValidationError(true);
        setFeedback({
          message: hasParcelaZerada
            ? 'Há parcela(s) com valor zerado. Ajuste os valores das parcelas antes de salvar.'
            : `A soma das parcelas (${fmt(somaParcelas)}) precisa ser igual ao total do pedido (${fmt(orderTotal)}).`,
          isOpen: true,
          statusOk: false,
        });
        return;
      }
    }

    const {
      paymentConditionName,
      paymentMethodId,
      paymentMethodName,
      paymentMethodDescription,
      ...orderData
    } = data as any;

    const paymentMethodsToSend = paymentMethodId
      ? [
        {
          paymentMethodId,
          ...(paymentMethodName === 'Outros' && paymentMethodDescription
            ? { description: paymentMethodDescription }
            : {}),
        },
      ]
      : undefined;

    const installmentsToSend = Array.isArray(data.installments)
      ? data.installments.map((parcela: any, idx: number) => {
        const comissaoAtiva = !!parcela.comissaoAtiva;
        return {
          numero: idx + 1,
          valor: Number(parcela.valor) || 0,
          vencimento: parcela.vencimento,
          comissaoAtiva,
          ...(comissaoAtiva ? { comissaoPercentual: Number(parcela.comissaoPercentual) || 0 } : {}),
        };
      })
      : undefined;

    const dataToSubmit = {
      ...orderData,
      items: savedItems,
      ...(installmentsToSend && installmentsToSend.length > 0
        ? { installments: installmentsToSend }
        : { installments: undefined }),
      paymentMethods: paymentMethodsToSend,
    };
    window.__lastOrderPayload = JSON.parse(JSON.stringify(dataToSubmit));
    console.log('🚀 [DEBUG] Payload enviado para o backend (criação de pedido):', dataToSubmit);
    setHasValidationError(false);
    submitOrder(dataToSubmit);
  }, [submitOrder]);

  const onInvalidSubmit = useCallback((errors: any) => {
    console.error('ERROS DE VALIDAÇÃO DO FORMULÁRIO:', errors);
  }, [methods]);

  const handleCustomSubmit = useCallback(() => {
    const formData = methods.getValues();
    const allItems = formData.items || [];
    const savedItems = allItems.filter(item => item.isSaved === true);

    if (savedItems.length === 0) {
      setHasValidationError(true);

      if (allItems.length === 0) {
        setNoSavedItemsType('no-items');
      }
      else {
        setNoSavedItemsType('not-saved');
      }
      setIsNoItemsModalOpen(true);
      return;
    }

    setHasValidationError(false);
    handleSubmit(onValidSubmit, onInvalidSubmit)();
  }, [methods, handleSubmit, onValidSubmit, onInvalidSubmit]);

  useEffect(() => {
    setSaveOrderHandler(handleCustomSubmit);

    return () => {
      setSaveOrderHandler(null);
    };
  }, [setSaveOrderHandler, handleCustomSubmit]);

  const handleAddNewItem = () => {
    if (noSavedItemsType === 'no-items') {
      setHasValidationError(false);
    }
    append(itemInitialState);
  };

  const handleToggleItem = useCallback((index: number, forceOpen?: boolean) => {
    setCollapsedItems(prev => {
      const newSet = new Set(prev);
      if (forceOpen) {
        newSet.delete(index);
      } else {
        if (newSet.has(index)) {
          newSet.delete(index);
        } else {
          newSet.add(index);
        }
      }
      return newSet;
    });
  }, [collapsedItems]);

  const handleUpdateItem = useCallback((index: number, value: any) => {
    if (value.isSaved && hasValidationError && noSavedItemsType === 'not-saved') {
      setHasValidationError(false);
    }
    const currentItems = methods.getValues('items') || [];
    const newItems = [...currentItems];
    newItems[index] = value;
    methods.setValue('items', newItems, { shouldDirty: true, shouldValidate: false });
  }, [hasValidationError, noSavedItemsType, methods]);



  useEffect(() => {
    if (isEditMode && existingOrder) {
      const safeOrder: OrderSchemaType = {
        ...existingOrder,
        supplier: existingOrder.supplier === null ? '' : existingOrder.supplier,
        supplierId: existingOrder.supplierId === null ? undefined : existingOrder.supplierId,
        seller: existingOrder.seller === null ? '' : existingOrder.seller,
        sellerId: existingOrder.sellerId === null ? undefined : existingOrder.sellerId,
        client: existingOrder.client === null ? '' : existingOrder.client,
        clientId: existingOrder.clientId === null ? undefined : existingOrder.clientId,
        supplierDetails: existingOrder.supplierDetails ? {
          ...existingOrder.supplierDetails,
          companyName: existingOrder.supplierDetails.companyName || existingOrder.supplierDetails.name || ''
        } as Record<string, any> : undefined,
        clientDetails: existingOrder.clientDetails ? {
          ...existingOrder.clientDetails,
          companyName: existingOrder.clientDetails.companyName || existingOrder.clientDetails.name || ''
        } as Record<string, any> : undefined,
        sellerDetails: existingOrder.sellerDetails ? {
          ...existingOrder.sellerDetails,
          name: existingOrder.sellerDetails.name || ''
        } as Record<string, any> : undefined,
        orderTypeId: existingOrder.orderTypeId ?? undefined,
        orderType: existingOrder.orderType ?? '',
        billingDate: existingOrder.billingDate ?? null,
        clientOrderNumber: existingOrder.clientOrderNumber ?? undefined,
        representativeId: existingOrder.representativeId ?? undefined,
        representativeName: existingOrder.representativeName ?? '',
        orderSituationId: existingOrder.orderSituationId ?? undefined,
        orderSituationName: existingOrder.orderSituationName ?? '',
        generalClassId: existingOrder.generalClassId ?? undefined,
        generalClassName: existingOrder.generalClassName ?? '',
        sellingPrice: existingOrder.sellingPrice ?? undefined,
        paymentConditionId: existingOrder.paymentConditionId ?? undefined,
        paymentConditionName: existingOrder.paymentConditionName ?? undefined,
        // Forma flatten no api.ts (mirror de paymentConditionName); backend inclui OrderPaymentMethod no findOne.
        paymentMethodId: existingOrder.paymentMethodId ?? undefined,
        paymentMethodName: existingOrder.paymentMethodName ?? undefined,
        paymentMethodDescription: existingOrder.paymentMethodDescription ?? undefined,
        shippingTypeId: existingOrder.shippingTypeId ?? undefined,
        shippingTypeName: existingOrder.shippingTypeName ?? undefined,
        shippingValue: existingOrder.shippingValue ?? 0,
        items: existingOrder.items.map(item => ({
          ...item,
          isSaved: true,
          priceDetails: {
            ...item.priceDetails,
            sellingPrice: item.sellingPrice ?? undefined,
          },
          subItems: item.subItems?.map((subItem: any) => ({
            ...subItem,
            isSaved: true,
            priceDetails: {
              ...subItem.priceDetails,
              sellingPrice: subItem.sellingPrice ?? undefined,
            },
          })) || []
        }))
      };
      methods.reset(safeOrder);
      if (collapsedItems.size === 0 || collapsedItems.size === 1) {
        const initialCollapsedState = new Set<number>();
        safeOrder.items.forEach((_, index) => {
          initialCollapsedState.add(index);
        });
        setCollapsedItems(initialCollapsedState);
      }
    }
  }, [existingOrder, isEditMode, methods]);

  const isLoadingData = servicesQuery.isLoading || scriptsQuery.isLoading || (isEditMode && isLoadingOrder);

  if (isLoadingData) {
    const loadingMessage = isEditMode ? 'Carregando dados do pedido...' : 'Carregando dados do formulário...';
    return <div>{loadingMessage}</div>;
  }

  return (
    <FormProvider {...methods}>
      {isNoItemsModalOpen && (
        <ConfirmationModal
          isOpen={isNoItemsModalOpen}
          title={noSavedItemsType === 'no-items'
            ? "Atenção: Pedido sem itens"
            : "Atenção: Item não salvo"
          }
          message={noSavedItemsType === 'no-items'
            ? "É necessário que o pedido tenha pelo menos um item. Clique em adicionar novo item e adicione ao pedido."
            : "É necessário que você adicione pelo menos um item ao pedido. Clique no botão 'Adicionar ao pedido' dentro do item."
          }
          actions={[
            {
              label: 'Entendi',
              onClick: () => {
                setIsNoItemsModalOpen(false);
              },
              variant: 'primary'
            }
          ]}
        />
      )}

      {isValidationModalOpen && (
        <ConfirmationModal
          isOpen={isValidationModalOpen}
          title="Existem erros no formulário"
          message="Por favor, verifique os campos destacados em vermelho e corrija os erros antes de continuar."
          actions={[
            {
              label: 'Entendi',
              onClick: () => {
                setIsValidationModalOpen(false);
              },
              variant: 'primary'
            }
          ]}
        />
      )}
      <ApiDataProvider
        services={servicesQuery.data ?? []}
        suppliers={[]}
        scripts={scriptsQuery.data ?? []}
      >
        <Container>
          <form onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)}>
            <Card>
              <MainInfosSection />

              <DetailsSection />
            </Card>

            {fields.map((field: any, index: number) => (
              <Card key={field.id}>
                <ItemsSection
                  index={index}
                  onDelete={() => remove(index)}
                  updateItem={handleUpdateItem}
                  isCollapsed={collapsedItems.has(index)}
                  onToggle={(forceOpen?: boolean) => handleToggleItem(index, forceOpen)}
                  itemOptionsFromApi={[]}
                  formatOptionsList={[]}
                  finishingOptionsList={[]}
                  highlightSaveButton={hasValidationError && noSavedItemsType === 'not-saved'}
                />
              </Card>
            ))}

            <AddNewItemButton>
              <Button
                type="button"
                variant="outline"
                onClick={handleAddNewItem}
                style={{
                  borderColor: (hasValidationError && noSavedItemsType === 'no-items') ? '#F87171' : undefined,
                  borderWidth: (hasValidationError && noSavedItemsType === 'no-items') ? '2px' : undefined
                }}
              >
                <Plus size={16} />
                Adicionar Novo Item
              </Button>
            </AddNewItemButton>

            <Card>
              <FreteSection />
            </Card>

            <Card>
              <PaymentSection />
            </Card>
          </form>
        </Container>
      </ApiDataProvider>
    </FormProvider>
  );
};

export default AddOrder;